import React, { Fragment } from 'react';
import moment from 'moment';
import { Message } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import ChatMessage from './ChatMessage';
import ChatPostSummary from './ChatPostSummary';
import ExternalChatMessage from './ExternalChatMessage';

const SHOW_AVATAR_IF_MESSAGE_IS_AFTER_MINUTES = 3;

interface MessageSequenceProps {
    messages: TypedMap<Message>[];
    sessionKey: ChatSessionKey;
    showContextMenu?: boolean;
}

const MessageSequence = ({
    messages,
    sessionKey,
    showContextMenu = true,
}: MessageSequenceProps) => {
    let sentDate: string;

    return (
        <>
            {messages.map((message, index) => {
                if (message.get('type') === 'post') {
                    return (
                        <Fragment key={message.get('key')}>
                            <ChatPostSummary
                                sessionKey={sessionKey}
                                message={message}
                            />
                        </Fragment>
                    );
                }

                if (message.get('type') === 'external') {
                    const previous = index > 0 ? messages[index - 1] : null;
                    const previousIsSameExternal =
                        previous?.get('type') === 'external' &&
                        previous?.getIn(['from', 'name']) ===
                            message.getIn(['from', 'name']);
                    const previousSent = moment(previous?.get('sent'));
                    const sameNameWithinWindow =
                        previousIsSameExternal &&
                        moment(message.get('sent')).diff(
                            previousSent,
                            'minutes',
                        ) < SHOW_AVATAR_IF_MESSAGE_IS_AFTER_MINUTES;
                    return (
                        <Fragment key={`${sessionKey}-${message.get('key')}`}>
                            <ExternalChatMessage
                                message={message}
                                showAvatar={!sameNameWithinWindow}
                            />
                        </Fragment>
                    );
                }

                const isPrevious =
                    index !== 0 &&
                    messages[index - 1].get('type') !== 'post' &&
                    messages[index - 1].get('type') !== 'external' &&
                    messages[index - 1].getIn(['from', 'key']) ===
                        message.getIn(['from', 'key']);

                const previousMessageDate = moment(sentDate);
                const latestMessageDate = moment(message.get('sent'));
                sentDate = message.get('sent');

                const showAvatar =
                    !isPrevious ||
                    latestMessageDate.diff(previousMessageDate, 'minutes') >=
                        SHOW_AVATAR_IF_MESSAGE_IS_AFTER_MINUTES;

                return (
                    <Fragment key={`${sessionKey}-${message.get('key')}`}>
                        <ChatMessage
                            sessionKey={sessionKey}
                            showAvatar={showAvatar}
                            message={message}
                            showContextMenu={showContextMenu}
                        />
                    </Fragment>
                );
            })}
        </>
    );
};

export default MessageSequence;
